import {AsyncPipe} from "@angular/common";
import {
    AfterViewInit,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

import {Annotation, AnnotationService} from "@app/annotation/annotation.service";
import {AssetService} from "@app/asset";
import {PreferencesService} from "@app/preferences/preferences.service";
import {
    AnnotationTool,
    AssetBounds,
    CursorAction,
    HistoryAction,
    PresentingState,
    Slot,
    UserCursor
} from "@app/review/models";
import {DATE_OFFSET, RemoteStroke, RemoteStrokeHistory} from "@app/review/remote-stroke-history";
import {ReviewVideoService} from "@app/review/review-video.service";
import {ReviewService} from "@app/review/review.service";
import {StageService} from "@app/review/stage.service";
import {TimeScale} from "@app/review/timescale";
import {MessageType} from "@app/websocket/websocket.service";
import {Point, Rect} from "@shared/euclid";
import {getStroke} from 'perfect-freehand';
import {combineLatestWith, firstValueFrom, Observable} from "rxjs";
import {filter, map, startWith} from "rxjs/operators";

class StageVM {
    presentingState: PresentingState;
    userCursor: UserCursor;
    assetBounds: AssetBounds;
}

@Component({
  selector: 'frog-stage',
    imports: [
        AsyncPipe
    ],
  templateUrl: './stage.component.html',
  styleUrl: './stage.component.scss'
})
export class StageComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() tool: AnnotationTool;
    @ViewChild('canvas') canvas: ElementRef;
    @ViewChild('yondu') yondu: ElementRef;

    public vm$: Observable<StageVM>;
    public annotations$: Observable<Annotation[]>;

    public viewport: Rect;
    public mousePos: Point;
    public mouseRot: number;
    public enableYondu: boolean;
    public cursor: string;

    private isMouseDown: boolean;
    private _ctx: CanvasRenderingContext2D;
    private _assetBounds: AssetBounds;
    private _resizeObserver?: ResizeObserver;
    private readonly Debug: boolean = false;
    private readonly _laserDuration: number = 300;
    private readonly _saveTimeout: number = 1000;
    private _saveTimeoutId: number;

    // Remote Strokes
    private _remoteStrokeHistory: RemoteStrokeHistory;
    private _remoteStroke: RemoteStroke;
    private _inputPoints: number[][];
    private _strokeSize: number;
    private _strokeColor: string;
    private _annotationImages: Map<string, Map<number, VideoFrame>>;
    private _assetId: string;
    private _activeSlot: Slot = Slot.Primary;
    private _assetChanged: boolean;
    private _frame: number;
    private _timestamp: number;
    private _timeScale: TimeScale;

    constructor(
        private stageService: StageService,
        private reviewService: ReviewService,
        private preferencesService: PreferencesService,
        private annotationService: AnnotationService,
        private reviewVideoService: ReviewVideoService,
        private assetService: AssetService,
        private host: ElementRef,
        private destroyRef: DestroyRef,
    ) {
        this.viewport = new Rect();

        this._inputPoints = [];

        this._remoteStrokeHistory = new RemoteStrokeHistory();

        this._annotationImages = new Map<string, Map<number, VideoFrame>>();

        this.enableYondu = false;
        this.mousePos = new Point();

        effect(() => {
            const prefs = this.preferencesService.immediatePrefsSignal();
            this._strokeColor = prefs.annotation_color || '#ffffff';
            this._strokeSize = prefs.annotation_size || 4;

            this.setBrushCursorSize();
        });

        effect(() => {
            const strokeHistory = this.stageService.historySignal();
            if (strokeHistory.Action === HistoryAction.Clear) {
                this.clear(false);
            }
            else if (strokeHistory.Action === HistoryAction.Undo) {
                this.undo(false);
            }
            else if (strokeHistory.Action === HistoryAction.Redo) {
                this.redo(false);
            }
        });

        effect(() => {
            const _ = this.stageService.assetChangedSignal();
            this._assetChanged = true;
        })
    }

    ngOnInit() {
        this.vm$ = this.reviewService.presenterState$
            .pipe(
                combineLatestWith(
                    this.stageService.stage$.pipe(startWith(null)),
                    this.reviewService.assetBounds$,
                ),
                map(([presentingState, userCursor, assetBounds]) => {
                    this._assetBounds = assetBounds;

                    if (userCursor !== null) {
                        const remoteStroke = this._remoteStrokeHistory.getOrCreateStroke(userCursor);

                        if (userCursor.Point) {
                            remoteStroke.segments.push(userCursor.Point);
                        }

                        if (userCursor.Points) {
                            remoteStroke.segments = remoteStroke.segments.concat(userCursor.Points);
                        }

                        if (userCursor.Action === CursorAction.Start) {
                            window.clearTimeout(this._saveTimeoutId);
                        }

                        if (userCursor.Action === CursorAction.End && remoteStroke.tool === AnnotationTool.Cursor) {
                            this._remoteStrokeHistory.deleteStroke(remoteStroke.id);
                        }
                    }

                    this._remoteStrokeHistory.computeLocalPoints(this._assetBounds.relativeBounds);

                    return {presentingState, userCursor, assetBounds};
                })
            );

        this.annotationService.annotations$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(annotations => {
                if (!this._assetChanged)
                    return;

                this._assetChanged = false;
                this.clear(false);
                this.preloadAnnotationImages(annotations, this._timeScale);
            });

        // Only the active slot's playback drives the Stage overlay - annotations/cursors are active-slot-
        // only (§3.4). this._activeSlot is kept current by the reviewState$ subscription below and read
        // here on every emission, so it tracks slot switches without needing to resubscribe.
        this.reviewVideoService.playbackState$
            .pipe(
                filter(playbackState => playbackState.slot === this._activeSlot),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(playbackState => {
                this._frame = playbackState.frame;
                this._timestamp = playbackState.timeScale.frameToTime(this._frame) || 0;
                this._timeScale = playbackState.timeScale;
            });

        this.reviewService.reviewState$
            .pipe(
                filter(i => i !== null),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((reviewState) => {
                this._activeSlot = reviewState.ActiveSlot ?? Slot.Primary;
                this._assetId = this._activeSlot === Slot.Compare ? reviewState.CompareAsset : reviewState.Asset;
            });

        this.render();
    }

    ngOnDestroy() {
        this._resizeObserver.disconnect();
    }

    ngAfterViewInit() {
        this._ctx = this.canvas.nativeElement.getContext('2d');
        this._resizeObserver = new ResizeObserver(() => {
            this.viewport = Rect.fromDomRect(this.host.nativeElement.getBoundingClientRect());
            this._remoteStrokeHistory.computeLocalPoints(this._assetBounds?.relativeBounds);
        });

        this._resizeObserver.observe(this.host.nativeElement);
    }

    @HostListener('window:mousemove', ['$event'])
    mouseMove(event: PointerEvent): void {
        if (this.isMouseDown) {
            if (event.movementX === 0 && event.movementY === 0) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            const point = new Point(event.clientX, event.clientY);
            const virtualPoint = this._assetBounds.bounds.toViewportSpace(point);
            const pos = this._assetBounds.relativeBounds.fromViewportSpace(virtualPoint);

            // Yondu
            if (this._inputPoints.length > 1) {
                this.mousePos = new Point(pos.x - 16, pos.y - 16);
                const dx = pos.x - this._inputPoints[this._inputPoints.length - 1][0];
                const dy = pos.y - this._inputPoints[this._inputPoints.length - 1][1];
                const rad = Math.atan2(dy, dx);
                this.mouseRot = rad * 180 / Math.PI + 135;
            }

            const segment = [
                pos.x,
                pos.y,
                event.pressure,
                Date.now() - DATE_OFFSET,
            ]

            if (event.shiftKey && this._inputPoints.length > 1) {
                const x = this._inputPoints[1][0] - this._inputPoints[0][0];
                const y = this._inputPoints[1][1] - this._inputPoints[0][1];
                if (x >= y) {
                    segment[1] = this._inputPoints[0][1];
                }
                else {
                    segment[0] = this._inputPoints[0][0];
                }
            }

            this._inputPoints = [...this._inputPoints, segment];
            this._remoteStroke.segments = [...this._inputPoints, segment];

            this.sendStrokeData(event, segment[3] as number);
        }
    }

    @HostListener('window:mouseup')
    mouseUp(event: MouseEvent) {
        this.isMouseDown = false;
        this.enableYondu = false;

        if (!this._remoteStroke) {
            return;
        }

        // Convert to stored remote stroke
        const remotePoints = [];
        for (let i = 0; i < this._remoteStroke.segments.length; i++) {
            const segment = this._remoteStroke.segments[i];
            const point = new Point(segment[0], segment[1]);
            const virtualPoint = this._assetBounds.relativeBounds.toViewportSpace(point);
            remotePoints.push([virtualPoint.x, virtualPoint.y, segment[2], segment[3]]);
        }
        this._remoteStroke.segments = remotePoints;
        // Set color to current just in case there was a delay in a color change
        this._remoteStroke.color = this._strokeColor;

        this._remoteStrokeHistory.addStroke(this._remoteStroke);
        if (this._remoteStroke.tool !== AnnotationTool.Cursor) {
            this._remoteStrokeHistory.historyPointer += 1;
        }

        this._inputPoints = [];

        this.sendStrokeEnd();

        this._remoteStrokeHistory.computeLocalPoints(this._assetBounds.relativeBounds);

        this._remoteStroke = null;

        if (this.tool === AnnotationTool.Pencil || this.tool === AnnotationTool.Eraser) {
            this.startSaveTimeout();
        }
    }

    mouseDown(event: MouseEvent): void {
        if (event.button === 0) {
            if (!this.isAssetBoundsReady()) {
                // Bounds are still from the previously-displayed asset (viewer swap in flight) - ignore the
                // pointer event rather than drawing against a coordinate mapping for the wrong asset.
                return;
            }

            this.isMouseDown = true;

            window.clearTimeout(this._saveTimeoutId);

            this._inputPoints = [];

            this._remoteStroke = new RemoteStroke();
            this._remoteStroke.segments = this._inputPoints;
            this._remoteStroke.size = this._strokeSize;
            this._remoteStroke.color = this._strokeColor;
            this._remoteStroke.tool = this.tool;
            this._remoteStroke.frame = this._frame;

            // When we start a new stroke, we need to truncate the remoteStrokes history
            this._remoteStrokeHistory.truncateStrokes(this._remoteStrokeHistory.historyPointer);

            this.sendStrokeStart();
            this.enableYondu = this._strokeColor === '#ff0000ff' && this._strokeSize === 2 && this.tool === AnnotationTool.Cursor;
        }
    }

    public clear(send: boolean = true): void {
        this._remoteStrokeHistory.clear();

        const annotationAsset = this._annotationImages.get(this._assetId);
        if (annotationAsset) {
            annotationAsset.delete(this._frame);
        }

        if (send) {
            const data = {
                Action: HistoryAction.Clear,
            }
            this.reviewService.send(data, MessageType.StrokeHistory);

            this.removeAnnotationImage();
        }
    }

    public undo(send: boolean = true): void {
        if (this._remoteStrokeHistory.Strokes.length === 0) {
            return;
        }

        if (this._remoteStrokeHistory.historyPointer === -1) {
            // Nothing to undo
            return;
        }

        this._remoteStrokeHistory.historyPointer -= 1;

        if (send) {
            const data = {
                Action: HistoryAction.Undo,
                HistoryPosition: this._remoteStrokeHistory.historyPointer,
            }
            this.reviewService.send(data, MessageType.StrokeHistory);

            this.startSaveTimeout();
        }
    }

    public redo(send: boolean = true): void {
        const strokes = this._remoteStrokeHistory.Strokes;
        if (strokes.length === 0) {
            return;
        }

        if (this._remoteStrokeHistory.historyPointer === strokes.length - 1) {
            // Nothing to redo
            return;
        }

        this._remoteStrokeHistory.historyPointer += 1;

        if (send) {
            const data = {
                Action: HistoryAction.Redo,
                HistoryPosition: this._remoteStrokeHistory.historyPointer,
            }
            this.reviewService.send(data, MessageType.StrokeHistory);

            this.startSaveTimeout();
        }
    }

    private render(): void {
        if (this._ctx) {
            this._ctx.clearRect(0, 0, this.viewport.width, this.viewport.height);

            this.renderDebug();

            // Clip to asset
            this._ctx.save();
            if (this._assetBounds && this._assetBounds.relativeBounds) {
                this._ctx.beginPath();
                this._ctx.rect(this._assetBounds.relativeBounds.x, this._assetBounds.relativeBounds.y, this._assetBounds.relativeBounds.width, this._assetBounds.relativeBounds.height);
                this._ctx.clip();
            }

            this.renderAnnotationImage();

            this.renderStrokes();

            const now = Date.now() - DATE_OFFSET;
            this.renderCursors(now);
            this.renderLocalStrokes(now);

            this._ctx.restore();
        }

        requestAnimationFrame(() => this.render());
    }

    private drawStroke(ctx: CanvasRenderingContext2D|OffscreenCanvasRenderingContext2D, points: number[][], size: number, color: string, tool: AnnotationTool): void {
        const stroke = getStroke(points, {
            size: (size * 2) / this._assetBounds.scale,
            simulatePressure: false,
            start: {
                cap: true,
                taper: tool === AnnotationTool.Cursor,
            }
        });

        const pathData = new Path2D(this.getSvgPathFromStroke(stroke));

        if (tool === AnnotationTool.Eraser) {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = '#000';
        }
        else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = color;
        }
        ctx.fill(pathData);

        ctx.globalCompositeOperation = 'source-over';
    }

    private sendStrokeData(event: MouseEvent, time: number) {
        if (!this._assetBounds) {
            return;
        }

        let virtualPoint = new Point();
        if (event) {
            const point = new Point(event.clientX, event.clientY);
            virtualPoint = this._assetBounds.bounds.toViewportSpace(point);
        }

        const data = {
            Id: this._remoteStroke.id,
            Action: CursorAction.Push,
            Point: [...virtualPoint.toArray(), 0, time],
        }
        this.reviewService.send(data, MessageType.UserCursor);
    }

    private sendStrokeStart() {
        const data = {
            Id: this._remoteStroke.id,
            Action: CursorAction.Start,
            Color: this._strokeColor,
            Size: this._strokeSize,
            Time: this._remoteStroke.startTime,
            Frame: this._frame,
            Tool: this.tool,
        }
        this.reviewService.send(data, MessageType.UserCursor);
    }

    private sendStrokeEnd() {
        const data = {
            Id: this._remoteStroke.id,
            Action: CursorAction.End,
        }
        this.reviewService.send(data, MessageType.UserCursor);
    }

    // Guards against drawing with bounds still computed for a since-swapped-away asset (e.g. image->video
    // switch) - AssetBounds.assetId is only trustworthy once it matches the asset currently on screen.
    private isAssetBoundsReady(): boolean {
        return Boolean(this._assetBounds) && this._assetBounds.assetId === this._assetId;
    }

    private average(a: number, b: number) {
        return (a + b) / 2;
    }

    private getSvgPathFromStroke(points, closed = true) {
        const len = points.length

        if (len < 4) {
            return ``
        }

        let a = points[0]
        let b = points[1]
        const c = points[2]

        let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(
            2
        )},${b[1].toFixed(2)} ${this.average(b[0], c[0]).toFixed(2)},${this.average(
            b[1],
            c[1]
        ).toFixed(2)} T`

        for (let i = 2, max = len - 1; i < max; i++) {
            a = points[i]
            b = points[i + 1]
            result += `${this.average(a[0], b[0]).toFixed(2)},${this.average(a[1], b[1]).toFixed(
                2
            )} `
        }

        if (closed) {
            result += 'Z'
        }

        return result
    }

    public renderToCanvas() {
        const canvas = new OffscreenCanvas(this._assetBounds.assetSize.width, this._assetBounds.assetSize.height);
        const ctx = canvas.getContext('2d');
        const assetRect = new Rect(0, 0, this._assetBounds.assetSize.width, this._assetBounds.assetSize.height);

        const annotationAsset = this._annotationImages.get(this._assetId);
        if (annotationAsset) {
            const annotationImage = annotationAsset.get(this._frame);
            if (annotationImage) {
                ctx.drawImage(
                    annotationImage,
                    0,
                    0,
                );
            }
        }

        const strokes = this._remoteStrokeHistory.strokesForFrame(this._frame);
        for (let i = 0; i < strokes.length; i++) {
            const remoteStroke = strokes[i];
            if (i > this._remoteStrokeHistory.historyPointer) {
                break;
            }

            const points = remoteStroke.segments.map(p => {
                const point = new Point(p[0], p[1]);
                const localPoint = assetRect.fromViewportSpace(point);

                return [localPoint.x, localPoint.y, p[2], p[3]];
            });

            this.drawStroke(ctx, points, remoteStroke.size, remoteStroke.color, remoteStroke.tool);
        }

        canvas.convertToBlob({type: 'image/png'})
            .then(blob => {
                const annotation = this.annotationService.getFromTimestamp(this._timestamp);
                let annotationRequest;

                const asset = firstValueFrom(this.assetService.resolve([this._assetId]))
                    .then(assets => {
                        const notify = Boolean(this.reviewService.getUserById(assets[0].author.id));

                        if (annotation) {
                            annotationRequest = this.annotationService.editImage(annotation.id, blob, notify);
                        }
                        else {
                            const annotation = new Annotation();
                            annotation.asset = this._assetId;
                            annotation.timestamp = this._timestamp || 0;
                            annotation.drawing = blob;

                            annotationRequest = this.annotationService.create(annotation, notify);
                        }

                        annotationRequest.subscribe(() => {
                            // Tell other clients
                            this.reviewService.send({}, MessageType.AnnotationChange);
                        });
                    });
            });
    }

    async loadImage(url) {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const decoder = new ImageDecoder({data: buffer, type: 'image/png'});
        const { image } = await decoder.decode();
        return image.clone();
    }

    async preloadAnnotationImages(annotations: Annotation[], timeScale: TimeScale) {
        if (annotations.length === 0) {
            return;
        }

        let annotationAsset = this._annotationImages.get(annotations[0].asset);
        if (!annotationAsset) {
            annotationAsset = new Map<number, VideoFrame>();
            this._annotationImages.set(annotations[0].asset, annotationAsset);
        }
        annotationAsset.clear();

        for (const annotation of annotations) {
            if (annotation.drawing) {
                const bitmap = await this.loadImage(annotation.drawing);
                const frame = timeScale.timeToFrame(annotation.timestamp) || 0;
                annotationAsset.set(frame, bitmap);
            }
        }
    }

    private renderDebug() {
        if (this.Debug) {
            this._ctx.strokeStyle = '#0000ff';
            this._ctx.strokeRect(0, 0, this.viewport.width, this.viewport.height);
            if (this._assetBounds && this._assetBounds.relativeBounds) {
                this._ctx.strokeStyle = '#00ff00';
                this._ctx.strokeRect(this._assetBounds.relativeBounds.x, this._assetBounds.relativeBounds.y, this._assetBounds.relativeBounds.width, this._assetBounds.relativeBounds.height);
            }
        }
    }

    private renderAnnotationImage() {
        if (!this._assetBounds.relativeBounds) {
            return;
        }

        const annotationAsset = this._annotationImages.get(this._assetId);
        if (!annotationAsset) {
            return;
        }

        const annotationImage = annotationAsset.get(this._frame);
        if (annotationImage) {
            this._ctx.drawImage(
                annotationImage,
                this._assetBounds.relativeBounds.x,
                this._assetBounds.relativeBounds.y,
                this._assetBounds.relativeBounds.width,
                this._assetBounds.relativeBounds.height,
            );
        }
    }

    private renderStrokes() {
        const strokes = this._remoteStrokeHistory.strokesForFrame(this._frame);
        for (let i = 0; i < strokes.length; i++) {
            const remoteStroke = strokes[i];
            if (i > this._remoteStrokeHistory.historyPointer) {
                break;
            }

            const segments = this._remoteStrokeHistory.localSegments(remoteStroke.id);
            if (!segments || segments.length < 2) {
                continue;
            }

            this.drawStroke(this._ctx, segments, remoteStroke.size, remoteStroke.color, remoteStroke.tool);
        }
    }

    private renderCursors(now: number) {
        const cursors = this._remoteStrokeHistory.Cursors;
        for (const remoteStroke of cursors) {
            let segments = this._remoteStrokeHistory.localSegments(remoteStroke.id);
            if (!segments || segments.length < 2) {
                continue;
            }

            segments = segments.filter(p => now - p[3] <= this._laserDuration);

            this.drawStroke(this._ctx, segments, remoteStroke.size, remoteStroke.color, remoteStroke.tool);
        }
    }

    private renderLocalStrokes(now: number) {
        let points = this._inputPoints;
        if (this.tool === AnnotationTool.Cursor && this._inputPoints.length > 0) {
            points = this._inputPoints.filter(p => now - p[3] <= this._laserDuration);
        }
        this.drawStroke(this._ctx, points, this._strokeSize, this._strokeColor, this.tool);
    }

    private removeAnnotationImage() {
        const annotation = this.annotationService.getFromTimestamp(this._timestamp);
        if (annotation) {
            this.annotationService.editImage(annotation.id, null)
                .subscribe();
        }
    }

    private startSaveTimeout() {
        window.clearTimeout(this._saveTimeoutId);
        this._saveTimeoutId = window.setTimeout(() => this.renderToCanvas(), this._saveTimeout)
    }

    private setBrushCursorSize() {
        const smallPadding = 8;
        const smallSize = this._strokeSize + smallPadding;
        const small = `
<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='${smallSize}' height='${smallSize}' viewBox='0 0 ${smallSize} ${smallSize}'>
  <line x1='${smallSize / 2}' y1='0' x2='${smallSize / 2}' y2='${smallSize}' stroke='white' stroke-width='2' />
  <line x1='0' y1='${smallSize / 2}' x2='${smallSize}' y2='${smallSize / 2}' stroke='white' stroke-width='2' />
  <circle cx='${smallSize / 2}' cy='${smallSize / 2}' r='1.5' stroke='black' stroke-width='1.5' fill='black' />
</svg>`;

        const large = `
<svg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' width='${this._strokeSize}' height='${this._strokeSize}' viewBox='0 0 ${this._strokeSize} ${this._strokeSize}'>
    <circle cx='${this._strokeSize / 2}' cy='${this._strokeSize / 2}' r='${(this._strokeSize - 1) / 2}' stroke='white' stroke-width='1.5' fill='none' />
    <circle cx='${this._strokeSize / 2}' cy='${this._strokeSize / 2}' r='${(this._strokeSize - 2) / 2}' stroke='black' stroke-width='1.5' fill='none' />
</svg>`

        const strokeSizeBreakpoint = 8;
        if (this._strokeSize < strokeSizeBreakpoint) {
            this.cursor = `url("data:image/svg+xml,${encodeURIComponent(small)}") ${this._strokeSize / 2} ${this._strokeSize / 2}, auto`;
        }
        else {
            this.cursor = `url("data:image/svg+xml,${encodeURIComponent(large)}") ${this._strokeSize / 2} ${this._strokeSize / 2}, auto`;
        }
    }
}
